import React from "react";
import PropTypes from "prop-types";
import { ArrowDown, ArrowUp, Badge, Button, Card, CardHeader, Menu, DotsVertical, Table, Tag } from "@opendate-app/setlist";
import { useStaffingDepartmentsContext } from "./StaffingDepartmentsContext";
import { departmentShape } from "./staffingShapes";
import { formatRate } from "./staffingFormatters";
import { inScope } from "./staffingScopes";

const DepartmentCard = ({ department, previousDepartment, nextDepartment, showArchived, onEditDepartment, onEditPosition }) => {
  const { permissions, permissionScopes, paths, mutate, pending } = useStaffingDepartmentsContext();
  const canManageDepartment = permissions.manageRoster && inScope(permissionScopes?.manageDepartmentIds, department.id);
  const canManagePosition = (position) => permissions.manageRoster && inScope(permissionScopes?.managePositionIds, position.id);
  const positions = department.positions.filter((position) => showArchived || !position.archived);
  const departmentPath = `${paths.departments}/${department.id}`;
  const positionPath = (position) => `${paths.positions}/${position.id}`;
  const leadsPath = `${departmentPath}/leads`;
  const leadMemberIds = new Set(department.leads.map((lead) => lead.staff_member_id));
  const leadCandidates = department.lead_candidates.filter((candidate) => !leadMemberIds.has(candidate.id));

  const addLead = (candidate) => mutate("post", leadsPath, { staff_member_id: candidate.id }, "Could not add this lead.");
  const removeLead = (lead) => mutate("delete", `${leadsPath}/${lead.id}`, undefined, "Could not remove this lead.");

  const moveDepartment = (target) => mutate("patch", departmentPath, { staff_department: { position: target.position } }, "Could not move this department.");
  const moveAction = (position, target) => mutate("patch", positionPath(position), { staff_position: { position: target.position } }, "Could not move this position.");
  const archiveDepartment = () => mutate("patch", `${departmentPath}/${department.archived ? "unarchive" : "archive"}`, {}, "Could not update this department.");
  const archivePosition = (position) => mutate("patch", `${positionPath(position)}/${position.archived ? "unarchive" : "archive"}`, {}, "Could not update this position.");

  const columns = [
    {
      key: "name",
      header: "Position",
      primary: true,
      render: (position) => (
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          {position.color_hex && <Badge color={position.color_hex} type="pill-outline" size="sm" dot>{position.name}</Badge>}
          {!position.color_hex && <span>{position.name}</span>}
          {position.archived && <Badge size="sm">Archived</Badge>}
        </div>
      ),
    },
    permissions.manageRates && {
      key: "rate",
      header: "Default rate",
      render: (position) =>
        position.volunteer ? (
          <Badge color="purple" size="sm">
            Volunteer
          </Badge>
        ) : (
          formatRate(position.default_hourly_rate) || "—"
        ),
    },
    !permissions.manageRates && {
      key: "type",
      header: "Type",
      render: (position) =>
        position.volunteer ? (
          <Badge color="purple" size="sm">
            Volunteer
          </Badge>
        ) : (
          "Paid"
        ),
    },
    { key: "staff", header: "People", align: "right", render: (position) => position.staff_count },
    { key: "templates", header: "In templates", align: "right", render: (position) => position.template_count },
    permissions.manageRoster && {
      key: "actions",
      header: "",
      align: "right",
      render: (position) => {
        if (!canManagePosition(position)) return null;
        const index = positions.indexOf(position);
        return (
          <Menu
            placement="bottom-end"
            trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`Actions for ${position.name}`} />}
            items={[
              { key: "edit", label: "Edit", onClick: () => onEditPosition(position) },
              { key: "up", label: "Move up", disabled: pending || index === 0, onClick: () => moveAction(position, positions[index - 1]) },
              { key: "down", label: "Move down", disabled: pending || index === positions.length - 1, onClick: () => moveAction(position, positions[index + 1]) },
              { key: "archive", label: position.archived ? "Restore" : "Archive", disabled: pending, onClick: () => archivePosition(position) },
            ]}
          />
        );
      },
    },
  ].filter(Boolean);

  const actions = canManageDepartment && (
    <div className="d-flex" style={{ gap: 4 }}>
      <Button variant="tertiary" size="sm" icon={ArrowUp} aria-label="Move department up" disabled={pending || !previousDepartment} onClick={() => moveDepartment(previousDepartment)} />
      <Button variant="tertiary" size="sm" icon={ArrowDown} aria-label="Move department down" disabled={pending || !nextDepartment} onClick={() => moveDepartment(nextDepartment)} />
      <Button variant="secondary" size="sm" onClick={() => onEditPosition({ staff_department_id: department.id })}>
        Add position
      </Button>
      <Menu
        placement="bottom-end"
        trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`Actions for ${department.name}`} />}
        items={[
          { key: "edit", label: "Rename", onClick: () => onEditDepartment(department) },
          !department.general && { key: "archive", label: department.archived ? "Restore" : "Archive", disabled: pending, onClick: archiveDepartment },
        ].filter(Boolean)}
      />
    </div>
  );

  return (
    <Card
      padding="md"
      style={{ marginBottom: 20 }}
      header={
        <CardHeader
          title={department.name}
          leading={
            department.color_hex ? (
              <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: "50%", background: department.color_hex }} />
            ) : undefined
          }
          badge={department.archived ? <Badge size="sm">Archived</Badge> : undefined}
          supportingText={`${positions.length} ${positions.length === 1 ? "position" : "positions"}`}
          actions={actions}
        />
      }
    >
      <div className="d-flex align-items-center flex-wrap" style={{ gap: 8, marginBottom: 12 }}>
        <span>Leads</span>
        {department.leads.length === 0 && <span className="text-muted">None yet</span>}
        {department.leads.map((lead) => (
          <Tag key={lead.id} size="sm" onDismiss={canManageDepartment && !pending ? () => removeLead(lead) : undefined}>
            {lead.name}
          </Tag>
        ))}
        {canManageDepartment && leadCandidates.length > 0 && (
          <Menu
            placement="bottom-start"
            trigger={
              <Button variant="tertiary" size="sm">
                Add lead
              </Button>
            }
            items={leadCandidates.map((candidate) => ({ key: candidate.id, label: candidate.name, disabled: pending, onClick: () => addLead(candidate) }))}
          />
        )}
      </div>
      <Table columns={columns} data={positions} getRowKey={(position) => position.id} empty="No positions in this department yet." />
    </Card>
  );
};

DepartmentCard.propTypes = {
  department: departmentShape.isRequired,
  previousDepartment: departmentShape,
  nextDepartment: departmentShape,
  showArchived: PropTypes.bool.isRequired,
  onEditDepartment: PropTypes.func.isRequired,
  onEditPosition: PropTypes.func.isRequired,
};

export default DepartmentCard;
